import { StrictMode, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './LocalDialog';
import { StaffMessageForm } from './Components/StaffMessageForm';
import { useThemeColors } from '@/Hooks/useThemeColors';
import { useInertBackground } from '@/Hooks/useInertBackground';
import { ensureRecaptcha } from '@/Public/StaffMessage/RecaptchaLoader';

const queryClient = new QueryClient();

interface StaffMessageProps {
    staffName: string;
    recipientId?: string;
    recipientAddress?: string;
    recaptchaSiteKey: string;
    primaryColor?: string;
    textColor?: string;
    linkColor?: string;
}

// Module-level setter populated when the component mounts.
let _open: ((props: StaffMessageProps) => void) | null = null;

const DEFAULT_PRIMARY = '#111827';
const DEFAULT_TEXT = '#ffffff';

function StaffMessageDialog() {
    const [isOpen, setIsOpen] = useState(false);
    const [dialogProps, setDialogProps] = useState<StaffMessageProps>({
        staffName: '',
        recaptchaSiteKey: '',
        primaryColor: '',
        textColor: '',
        linkColor: '',
    });

    // Incrementing this key remounts StaffMessageForm, giving a fresh state on every open.
    const [formKey, setFormKey] = useState(0);
    // Callback ref: when Radix mounts/unmounts the dialog content, this state
    // updates and triggers useThemeColors to re-run with the correct element.
    const [dialogEl, setDialogEl] = useState<HTMLDivElement | null>(null);

    // The element focused when the modal was opened (the "Send Message" trigger).
    // The modal is opened programmatically via window.openStaffMessageWidget, so
    // Radix's native trigger-based focus restoration doesn't apply — we restore it
    // manually in onCloseAutoFocus.
    const triggerRef = useRef<HTMLElement | null>(null);

    useThemeColors({
        primaryColor: dialogProps.primaryColor || DEFAULT_PRIMARY,
        textColor: dialogProps.textColor || DEFAULT_TEXT,
        element: dialogEl,
    });

    // Trap iOS VoiceOver's swipe gesture inside the dialog: Radix hides
    // background peers with aria-hidden, but that alone lets VoiceOver flick
    // into background content (e.g. the directory's "Results 1-12 of 25").
    useInertBackground(isOpen, dialogEl);

    // Set the link color
    useEffect(() => {
        if (dialogProps.linkColor) {
            dialogEl?.style?.setProperty('--color-intent-link-primary', dialogProps.linkColor);
            dialogEl?.style?.setProperty('--color-intent-link-primary-hover', `color-mix(in srgb, ${dialogProps.linkColor} 85%, black)`);
        }
    }, [dialogProps.linkColor, dialogEl]);

    useEffect(() => {
        _open = (props: StaffMessageProps) => {
            triggerRef.current = document.activeElement as HTMLElement | null;
            void ensureRecaptcha(props.recaptchaSiteKey).catch(() => {});
            setDialogProps(props);
            setFormKey((k) => k + 1);
            setIsOpen(true);
        };
        return () => {
            _open = null;
        };
    }, []);

    // Return focus to the trigger button when the modal closes (X or Escape) so
    // keyboard users don't get dumped at the top of the page.
    const handleCloseAutoFocus = (event: Event) => {
        const trigger = triggerRef.current;
        if (trigger && typeof trigger.focus === 'function' && trigger.isConnected) {
            event.preventDefault();
            // Defer to the next frame: useInertBackground clears the background's `inert`
            // in its effect cleanup, which React runs after this (descendant) callback.
            // focus() on a still-inert element is a no-op, so wait until it's released.
            requestAnimationFrame(() => {
                if (trigger.isConnected) {
                    trigger.focus();
                }
            });
        }
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <DialogContent ref={setDialogEl} aria-describedby={undefined} onCloseAutoFocus={handleCloseAutoFocus}>
                <DialogHeader onClose={() => setIsOpen(false)}>
                    <DialogTitle>Send A Message</DialogTitle>
                </DialogHeader>
                <StaffMessageForm
                    key={formKey}
                    staffName={dialogProps.staffName}
                    recipientId={dialogProps.recipientId}
                    recipientAddress={dialogProps.recipientAddress}
                    recaptchaSiteKey={dialogProps.recaptchaSiteKey}
                />
            </DialogContent>
        </Dialog>
    );
}

const container = document.createElement('div');
document.body.appendChild(container);
createRoot(container).render(
    <StrictMode>
        <QueryClientProvider client={queryClient}>
            <StaffMessageDialog />
        </QueryClientProvider>
    </StrictMode>,
);

window.openStaffMessageWidget = (props) => {
    _open?.(props);
};
